import type {
  StrapiMagazineArticle,
  StrapiMagazineArticleCategory,
} from '~/composables/types/strapi/strapi-magazine'
import type { LocalizableResource } from '~/composables/types/localizable'
import type { StrapiApiDefaultResponse } from '~/composables/types/strapi-api'
import {
  getStrapiArticlesUrl,
  getStrapiArticlesCategoryUrl,
} from '~/utils/strapiURLhelpers'
import { dynamicContentTransformer } from '~/utils/dynamicContentTransformer'
import { useStaticMetrics } from '~/composables/useStaticMetrics'
import { useStrapiConfig, buildStrapiUrl } from '~/composables/useStrapi'

export const useResolvedMagazinePage = (slug: string | undefined) => {
  const { locale } = useI18n()
  const strapiConfig = useStrapiConfig()
  const staticMetrics = useStaticMetrics()

  const fetchArticle = async (): Promise<StrapiMagazineArticle | null> => {
    const url = buildStrapiUrl(
      strapiConfig,
      getStrapiArticlesUrl(slug, locale.value)
    )

    try {
      const response = await $fetch<
        StrapiApiDefaultResponse<StrapiMagazineArticle[]>
      >(url, { headers: strapiConfig.headers })
      const article = response.data?.[0] ?? null
      if (!article) return null

      return dynamicContentTransformer(
        article,
        staticMetrics,
        locale.value
      ) as StrapiMagazineArticle
    } catch {
      return null
    }
  }

  const fetchCategory =
    async (): Promise<StrapiMagazineArticleCategory | null> => {
      const url = buildStrapiUrl(
        strapiConfig,
        getStrapiArticlesCategoryUrl(slug ?? '', locale.value)
      )

      try {
        const response = await $fetch<
          StrapiApiDefaultResponse<StrapiMagazineArticleCategory[]>
        >(url, { headers: strapiConfig.headers })
        return response.data?.[0] ?? null
      } catch {
        return null
      }
    }

  const cacheKey = `resolved-magazine-page-${slug}-${locale.value}`
  const articleCacheKey = `resolved-article-${slug}-${locale.value}`
  const categoryCacheKey = `resolved-article-category-${slug}-${locale.value}`

  return useAsyncData<
    LocalizableResource | null,
    Error,
    LocalizableResource | null
  >(
    cacheKey,
    async (): Promise<LocalizableResource | null> => {
      const article = await fetchArticle()
      if (article) return article as LocalizableResource

      const category = await fetchCategory()
      return (category as LocalizableResource) ?? null
    },
    {
      server: true,
      lazy: false,
      dedupe: 'defer',
      getCachedData: (key) =>
        useNuxtData(key).data.value ??
        useNuxtData(articleCacheKey).data.value ??
        useNuxtData(categoryCacheKey).data.value ??
        undefined,
    }
  )
}
